import { DISCOVERIES } from './discoveries';
import { CERTAINTY, THEMES } from './types';

const PLACES: Readonly<Record<string, readonly [name: string, clue: string, text: string]>> = {
  'landmark-waag-dam': [
    'De Waag op de Dam',
    'Goederen worden gewogen voordat ze worden verhandeld. Zoek de Waag die schuin voor het stadhuis staat.',
    'Dit is de eerste Waag op de Dam, in gebruik sinds 1341. Hier worden goederen gewogen voordat ze worden verhandeld. Niemand weet hoe het gebouw eruitzag, dus de grootte en de hoogte zijn een zorgvuldige schatting.',
  ],
  'landmark-vismarkt': [
    'De vismarkt',
    'Vis wordt verkocht aan twee lange rijen overdekte banken. Zoek ze op de Dam, aan de kop van het Damrak.',
    'Twee rijen overdekte banken met een gangpad ertussen. Zeevis wordt verkocht aan de kant van het IJ en riviervis aan de kant van het Rokin. De banken volgen het schilderij van 1538.',
  ],
  'landmark-dam': [
    'De Dam en de grote sluis',
    'Een stenen dam ligt dwars door de rivier de Amstel. Ga erop staan en zoek de ene grote boog waar het water doorheen gaat.',
    'Je staat op een stenen dam in de Amstel. In de dertiende eeuw verbond een dam de nederzettingen aan de twee oevers. Boten varen door een sluis: zoek de grote boog aan de kant van het Damrak.',
  ],
  'landmark-scheepskraan': [
    'De havenkraan',
    'Een heel hoge mast staat op een vlot in het IJ, dicht bij de rij palen. Roei naar de havenkraan.',
    'Een havenkraan op een vlot in het IJ. Hij wordt rond 1498 voor het eerst genoemd. De mast was 100 voet hoog: ongeveer 28 meter. Waar het vlot precies lag en hoe groot het was, is geschat.',
  ],
  'landmark-lijnbaan-boom': [
    'De lijnbanen',
    'Vier heel lange loodsen liggen naast elkaar buiten de oude stad, op de Lastage. Zoek de plek waar touw wordt gemaakt.',
    'Een lijnbaan is een lange loods om touw te maken. Het schilderij toont er vier naast elkaar, elk 300 meter lang. Deze is van Cornelis Pietersz Boom. Loop de hele lengte af en tel je stappen.',
  ],
  'landmark-lakenramen': [
    'De lakenramen',
    'Bij de Amstel ligt een omheind veld vol lange rijen donkere ramen. Zoek de plek waar wollen laken wordt gedroogd.',
    'Op deze ramen wordt het wollen laken van de stad gedroogd en opgespannen. De schilder tekende ongeveer 13 rijen in een omheind veld. De Raamgracht kreeg later haar naam van deze ramen.',
  ],
  'landmark-lucienerf-carpenter': [
    'Het erf van de timmerman',
    'Een huistimmerman kocht de punt van een klein eiland. Zoek een huis met een erf vol hout.',
    'Huistimmerman Jacob Jansz kocht dit erf op 16 december 1536. Een kaart van 1544 toont hier een huis en een houttuin. Of die er in 1538 al stonden is niet zeker: de verkoop zegt dat niet.',
  ],
  'landmark-scafferij-baerdsehuys': [
    'De timmertuin van de stad',
    'De stad heeft een eigen timmertuin, op een eiland bij de Grimburgwal. Roei naar de Scafferij.',
    'Dit is de Scafferij, de timmertuin van de stad zelf. De kaart van 1544 schrijft hier de naam. Je ziet een hoog gebouw met een lager gebouw erachter. Wat er nog meer op het erf stond is niet bekend.',
  ],
  'landmark-oude-stadhuis': [
    'Het stadhuis',
    'De stad wordt bestuurd vanuit een smal gebouw met een puntgevel en een toren. Zoek het op de Dam.',
    'Dit is het oude Stadhuis. Zoek de toren en de open rechtbank aan de voorkant, de vierschaar. Het volgt het schilderij van 1538. Hoe groot het echt was is niet bekend.',
  ],
  'landmark-sint-olofspoort': [
    'De poort die een gevangenis werd',
    'Een oude stadspoort wordt niet meer als poort gebruikt. Nu worden er mensen in opgesloten. Zoek haar naast de Sint-Olofskapel.',
    'De Sint Olofspoort was een stadspoort van ongeveer 1370. In 1538 is zij een gevangenis. Zoek de doorgang met de boog beneden en de gevangenisramen erboven.',
  ],
  'landmark-kolk-gate': [
    'De waterpoort onder de Zeedijk',
    'Een stenen huis staat boven het water, waar de Zeedijk het oversteekt. Loop erdoorheen of roei eronderdoor.',
    'Deze stenen poort draagt de Zeedijk over de Oudezijds Kolk. Het water stroomt verder door een ronde boog. Het schilderij en de kaart van 1544 tekenen haar allebei, met twee rijen ramen boven de boog.',
  ],
  'landmark-nieuwe-brug-west-tower': [
    'De toren bij de havenmond',
    'Een ronde stenen toren staat met zijn voet in het water, aan het westelijke einde van de Nieuwe Brug.',
    'Een ronde stenen toren om de havenmond te verdedigen. Het schilderij toont zijn voet in het water, twee rijen kleine ramen en een puntdak. Ernaast gaat een trap naar het water.',
  ],
  'landmark-nieuwe-brug-blockhouse': [
    'Het blokhuis op de Nieuwe Brug',
    'Een hoog, smal huis van planken staat op de lange brug over de havenmond.',
    'Een hoog, smal blokhuis van planken met een puntdak. Het schilderij en de kaart van 1544 tonen het op zijn palen. Het stond tot 1560 op de Nieuwe Brug.',
  ],
  'landmark-blokhuis-boom-west': [
    'Het wachthuis bij de boom',
    'Een rij palen sluit de haven af. Roei naar de opening en zoek het kleine stenen huis dat daar de wacht houdt.',
    'Een rij palen sluit de haven af. Schepen varen door één opening, de boom. Dit kleine stenen wachthuis staat ernaast op een houten vlonder, met een donker, steil dak.',
  ],
  'landmark-handboogdoelen': [
    'De doelen van de handboogschutters',
    'De schutters van de stad hebben een groot gebouw bij de stadsmuur, ten noorden van de Heiligewegspoort.',
    'Het gilde van de handboogschutters maakte dit gebouw rond 1512 af. Ze hebben een eigen schietbaan bij de stadsmuur. Een tekening uit 1536, gemaakt naar Cornelis Anthonisz, toont het gebouw.',
  ],
  'landmark-voetboogdoelen': [
    'De doelen van de voetboogschutters',
    'Naast de handboogschutters schiet een tweede gilde met een andere boog. Zoek hun eenvoudiger gebouw.',
    'Dit eenvoudiger gebouw is van het gilde van de voetboogschutters. Het heeft een eigen schietbaan, los van de handboogschutters ernaast. Vergelijk de twee gebouwen: welk is het grootst?',
  ],
  'landmark-oude-kerk': [
    'De Oude Kerk',
    'Zeelieden hebben een eigen beschermheilige. Zoek zijn grote kerk met de toren, tussen de havenhuizen.',
    'De Oude Kerk werd in 1306 gewijd aan Sint Nicolaas, de beschermheilige van zeelieden. De toren is van ongeveer 1325. Kijk omhoog: de spitse naald kwam er rond 1500 op.',
  ],
  'landmark-nieuwe-kerk': [
    'De Nieuwe Kerk',
    'De stad had een tweede grote kerk nodig. Zoek haar naast de Dam. Ze heeft geen toren.',
    'Er woonden steeds meer mensen in de stad, dus gaf de bisschop in 1408 toestemming voor een tweede parochiekerk. De Nieuwe Kerk heeft geen toren. Zoek in plaats daarvan het kleine torentje op het dak.',
  ],
  'landmark-heilige-stede': [
    'De Heilige Stede',
    'Pelgrims komen over de Heiligeweg naar één kapel. Zoek de plek van het mirakel.',
    'Mensen geloofden dat een gewijde hostie hier in 1345 intact bleef in het vuur. Er werd een kapel gebouwd en pelgrims kwamen over de Heiligeweg. Na de stadsbrand van 1452 werd de kapel herbouwd.',
  ],
  'landmark-begijnhofkapel': [
    'De kapel van het Begijnhof',
    'Gelovige vrouwen die hun eigen bezit houden wonen rond een hof met aan alle kanten water. Zoek hun kapel.',
    'De begijnen zijn katholieke vrouwen die hun eigen bezit houden en helpen bij zorg en onderwijs. Hun kapel heeft een kleine vierkante klokkentoren. Pas in 1607 wordt zij de Engelse Kerk.',
  ],
  'landmark-sint-pietersgasthuis': [
    'Het Sint Pietersgasthuis',
    'Wie zorgt er voor je als er geen familie in de buurt is? Zoek het gasthuis aan de Nes, gebouwd rond een open binnenplaats.',
    'Het gasthuis geeft eten, onderdak en zorg. Het heeft een bakkerij en een brouwerij, en vrouwen doen veel van het dagelijks werk. Het brood en het linnen op de binnenplaats laten dat werk zien. Waar elke tafel stond is onze schatting.',
  ],
  'landmark-eerste-weeshuis': [
    'Het eerste weeshuis',
    'Wezen hebben een huis in de Kalverstraat, ten noorden van de Heilige Stede. Zoek het grote huis naast de kapel.',
    'Dit is het eerste weeshuis van Amsterdam. De eerste regels zijn van 1523. In welk jaar het werd gesticht is onbekend. De schilder van 1538 tekende hier een groot huis, naast de kapel.',
  ],
  'landmark-leprozenhuis': [
    'Het Leprozenhuis',
    'Buiten de stad, aan de Sint Antoniesdijk, staat een ommuurd huis met een eigen kapel. Zoek het huis van de leprozen.',
    'Mensen met lepra wonen hier, apart van de stad. Iedereen was bang voor de ziekte, hoewel zij niet gemakkelijk wordt overgedragen. Het schilderij toont een ommuurd erf met een kapel en haar klokkentoren.',
  ],
  'landmark-kartuizerklooster': [
    'Het Kartuizerklooster',
    'Ver buiten de muren wonen monniken rond een groot hof, elk in een cel met een eigen tuin.',
    'Kartuizer monniken wonen hier rond een grote kloostergang, elk in een eigen cel met een tuin. Archeologen vonden de grootte van het hof in de grond. De schilder van 1538 liet dit klooster weg, dus veel ervan is onze schatting.',
  ],
};

const WORDS: Readonly<Record<string, string>> = {
  'Think like a historian': 'Denk als een historicus',
  'Try a question': 'Probeer een vraag',
  'Choose an answer, then see what the sources tell us.':
    'Kies een antwoord en ontdek wat de bronnen ons vertellen.',
  'That fits the evidence.': 'Dat past bij de bronnen.',
  'Look at the evidence and try again.': 'Bekijk de bronnen en probeer het nog eens.',
  'Evidence from this place': 'Wat deze plek ons vertelt',
  'Next question': 'Volgende vraag',
  'Start again': 'Opnieuw beginnen',
  [THEMES.trade]: 'Handel en werk',
  [THEMES.city]: 'Stad en verdediging',
  [THEMES.care]: 'Geloof en zorg',
  [CERTAINTY.high]: 'Zeker: het gebouw staat er nog of is opgemeten.',
  [CERTAINTY.medium]:
    'Vrij zeker: een oude kaart of een geschiedenisboek geeft de plek of de grootte.',
  [CERTAINTY.low]: 'Een zorgvuldige schatting: de grootte of de vorm is geschat.',
  'Discovery book': 'Ontdekboek',
  'Discovery book (K)': 'Ontdekboek (K)',
  'Sign in to keep the places you find in your book.':
    'Log in om de plekken die je vindt in je boek te bewaren.',
  'Not saved to your book yet. Trying again.':
    'Nog niet in je boek bewaard. We proberen het opnieuw.',
  'Open the discovery book': 'Ontdekboek openen',
  'You found a new place': 'Je hebt een nieuwe plek gevonden',
  'Not found yet': 'Nog niet gevonden',
  Found: 'Gevonden',
  'How do we know?': 'Hoe weten we dat?',
  'Places found': 'Gevonden plekken',
  'Sources of this place': 'Bronnen van deze plek',
  'Walk up to a place, or row to it, to add it to your book.':
    'Loop naar een plek toe, of roei ernaartoe, om haar in je boek te zetten.',
};

export const DUTCH_DISCOVERIES: Readonly<Record<string, string>> = {
  ...WORDS,
  ...Object.fromEntries(
    DISCOVERIES.flatMap(({ id, name, clue, text }) => {
      const dutch = PLACES[id];
      return dutch
        ? [
            [name, dutch[0]],
            [clue, dutch[1]],
            [text, dutch[2]],
          ]
        : [];
    })
  ),
};
